<template>
  <template v-if="!filters?.submittedMeta.hasGeographicalFilter">
    <DashboardMissingFilter />
  </template>
  <template v-else-if="isMissingData">
    <DashboardMissingData />
  </template>
  <template v-else-if="missingDataRooom">
    <DashboardMissingData />
  </template>
  <template v-else>
    <section class="kpi-section-header">
      <InfoLabel
        :title="t('dashboard__text__measurement_values')"
        :info="kpiInfo?.[locale] ?? t('dashboard__text__measurement_values-info')"
        :size="'mediumBold'"
        :fullscreen="sharedData?.fullscreen ?? false"
      />
    </section>
    <section class="kpi-section-content">
      <template v-for="query in kpiQueries" :key="query.kpis.map((x) => x.kpi).join('_')">
        <KPIQuery
          :query="query"
          :fullscreen="sharedData?.fullscreen ?? false"
          @missingData="missingDataRoom"
        />
      </template>
      <ColorLegend
        v-if="selectedKpiType && !sharedData?.fullscreen"
        class="legend"
        :title="currentKpiSettings?.title[locale]"
        :colorScale="currentKpiSettings?.colorScale ?? DEFAULT_COLOR_SCALE"
        :format="{ format: { type: 'intl-number', style: 'percent', maximumFractionDigits: 0 } }"
        v-bind="colorLegend?.props"
        :mean="selectedNumericValue ?? 0"
      />
    </section>
  </template>
</template>
<script lang="ts" setup>
import ColorLegend from '@/design-system/atoms/ColorLegend.vue'
import DashboardMissingFilter from './DashboardMissingFilter.vue'
import DashboardMissingData from './DashboardMissingData.vue'
import InfoLabel from '@/design-system/molecules/InfoLabel.vue'
import { computed, inject, onUnmounted, toRefs, watch, ref } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { useKpiQueries, useKpis } from '../composables/useKpis'
import { Kpi, KpiValue, LocaleString } from '@/api-client/models/utilization/Contracts'
import useColoredEntities, { ColoredEntityData } from '@/composables/useColoredEntities'
import { ColorScaleConfig } from '@/utils/colorScale'
import { ANT_DESIGN_COLORS } from '@/utils/rgba'
import KPIQuery from './KPIQuery.vue'
import { sharedDataKey } from '../../types'
import { useRealm } from '@/composables/useRealm'
import { useFeature } from '@/pinia/features'
import { useCurrentFeatureFilters } from '@/composables/useFilters'

type Props = {
  title: LocaleString
  info?: LocaleString
  kpis: Kpi[]
  isMissingData: boolean
}

defineProps<Props>()

const sharedData = inject(sharedDataKey)
const store = useStore()
const kpis = useKpis()
const filters = useCurrentFeatureFilters()
const { settings } = toRefs(useRealm())
const { locale, t } = useI18n()

const kpiInfo = computed(() => settings.value?.config?.utilization.kpiInformation)

const { selectedKpiType, selectedNumericValue, currentKpiSettings, selectedKpi } = toRefs(kpis)

const { kpiQueries } = useKpiQueries()

const { feature: colorLegend } = useFeature('home/analytics/analyticareas/usage/kpis/colorlegend')

const isColoredSpaceEnabled = computed(() => {
  return selectedKpiType.value !== undefined && store.getters.isPlanviewShowing
})
const isColoredBuildingsEnabled = computed(() => {
  if (!selectedKpiType.value) return false
  if (store.state.clickedItemType === 'building') return true
  if (store.state.clickedItemType !== 'complex' && store.state.clickedItemType !== 'realEstate')
    return false
  return true
})

const coloredEntitiesData = computed<ColoredEntityData[]>(() => {
  if (!selectedKpiType.value) return []
  return (
    (selectedKpi.value?.valuesBySplitBy ?? selectedKpi.value?.values)?.map(
      kpiValueToColoredEntityData
    ) ?? []
  )
})
// COLORED BUILDINGS
const coloredEntities = useColoredEntities()
const missingDataRooom = ref(false)

function missingDataRoom(missing: boolean) {
  missingDataRooom.value = missing
}
const submittedGeographicalFilters = computed(() => filters.value?.submittedGeographicalFilters)
watch(submittedGeographicalFilters, (room, newRoom) => {
  if (room !== newRoom) {
    missingDataRooom.value = false
  }
})
watch(
  coloredEntitiesData,
  () => {
    if (
      currentKpiSettings.value &&
      (isColoredBuildingsEnabled.value || isColoredSpaceEnabled.value)
    ) {
      coloredEntities.set({
        key: 'utilization',
        data: coloredEntitiesData.value,
        colorScale: currentKpiSettings.value?.colorScale ?? DEFAULT_COLOR_SCALE,
        title: currentKpiSettings.value?.title[locale.value] ?? '',
        unit: '%',
        formatter: (v) => Math.round(v * 100).toString()
      })
    } else {
      coloredEntities.reset()
    }
  },
  { immediate: true }
)
onUnmounted(() => coloredEntities.reset())

function kpiValueToColoredEntityData(kpiValue: KpiValue): ColoredEntityData {
  return {
    id: kpiValue.splitKey,
    meta: {
      numberOfSpaces: kpiValue.numberOfSpaces
    },
    value: kpiValue.value
  }
}

const DEFAULT_COLOR_SCALE: ColorScaleConfig = {
  type: 'INTERPOLATE',
  cutoffs: [
    {
      value: 0,
      color: ANT_DESIGN_COLORS['gray-2'].toHex()
    },
    {
      value: 0.5,
      color: ANT_DESIGN_COLORS['gray-7'].toHex()
    },
    {
      value: 1,
      color: ANT_DESIGN_COLORS['gray-12'].toHex()
    }
  ]
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.kpi-section-header {
  display: flex;
  padding: 2px;
  margin-top: 16px;
  margin-bottom: 16px;
  align-content: flex-start;
}
.kpi-section-content {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
}

.title-row {
  @include font-mixins.desktop-bold-label;
  color: variables.$color-greyscale-content;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 4px;
}

.under-development {
  @include font-mixins.desktop-regular-tiny;
  color: red;
}

.legend {
  position: fixed;
  top: 64px;
  right: 0;
  margin: 16px;
}
</style>
